'use strict';

define('vb/private/stateManagement/rules/rulesUtils',['vb/private/utils', 'vb/private/constants'], (Utils, Constants) => {
  class RulesUtils {
    /**
     * @typedef {import('vb/private/stateManagement/container').default} Container
     */

    /**
     * Generates one or more scope expression given a constant name. Customer extensions can only
     * have $base.constants.foo but a factory extension can have all these forms - $base.constants.foo,
     * $constants.foo, $<scopeName>.constants.foo
     * @param container
     * @param constant
     * @return {array} in the case of a base extension, the allowed scope expressions can be many
     */
    static exprsFromConstantName(container, constant) {
      const dotSuffix = `constants.${constant}`;
      const baseExpr = `$base.${dotSuffix}`;
      const baseScopeExpr = `$base.${Utils.getScopeName(container).split('$')[1]}.${dotSuffix}`;
      // TODO: how about $constants['foo'] variants
      return container.isExtension() ? [baseExpr, baseScopeExpr]
        : [baseExpr, baseScopeExpr, `${Utils.getScopeName(container)}.${dotSuffix}`, `$${dotSuffix}`];
    }

    /**
     * Given a constant expression (typically allowed expression for current scoped constant) returns the constant name.
     * Currently rules constants can only be from flow, page, fragment
     * @param {string} expr
     * @returns {string} returns the constant name or ''
     */
    static constantNameFromExpr(expr) {
      // matches all forms of valid constants expressions.
      // eslint-disable-next-line max-len
      const re = /(((\$base|\$page|\$fragment|\$flow)(\.constants))|(\$constants)|(\$base\.(page\.|fragment\.|flow\.)(constants)))\.([\\$A-Z_a-z][\\$A-Z_a-z0-9]*)$/g;
      const parts = re.exec(expr);
      if (parts && parts.length > 2) {
        return parts[0].slice(parts[1].length + 1); // parts[1].length includes '.'
      }
      return '';
    }

    /**
     * Given the expression and a variable name, if the str expression contains the variable name in the expression,
     * it is returned. This is used to when a constant has a defaultValue expression that contains an expression
     * referencing one or more variables.
     * @param {String} str expression
     * @param {String} name variable name
     * @returns {*}
     */
    static locateExprFromVar(str, name) {
      // matches all forms of variable expressions with the given name.
      // eslint-disable-next-line max-len
      const match = str.match(new RegExp(`(((\\$base|\\$page|\\$fragment|\\$layout)(\\.variables\\.))|(\\$variables\\.)|(\\$base\\.(page|fragment|layout)(\\.variables\\.)))${name}`));
      return match && match[0];
    }

    /**
     * there can be many ways to express a target constant. This method returns a consistent expr -
     * $base.constants.<name>
     * @param sourceExpr
     * @returns {string}
     */
    static getTargetExprFromAnotherExpr(sourceExpr) {
      const name = RulesUtils.constantNameFromExpr(sourceExpr);
      return `$base.constants.${name}`;
    }

    /**
     * Validates expression set on referencedObject property (can either be a constant or a variable on any
     * available scopes) and returns the variable object.
     * @param {string} expr
     * @param {Container} container where the rules are defined
     * @param {Object} availableContexts
     * @returns {Object} with two props -
     * 'variable' instance that represents the expr. the container can be obtained from the variable
     * 'name'
     * 'namespace'
     */
    static getVariableFromReferenced(expr, container, availableContexts) {
      const strip$ = (s) => (s.charAt(0) === '$' ? s.substring(1) : s);
      const tokens = (expr.split('.')) || [];
      let name = tokens.pop();
      // only variables or constants or (some) builtins can be specified in referencedContext. It cannot be
      // sub-props of variables etc.
      let namespace = ['variables', 'constants', '$variables', '$constants'].includes(tokens[tokens.length - 1])
        ? strip$(tokens.pop()) : undefined;
      const refContainer = container; // container where var is referenced

      let targetScope;
      if (tokens.length === 0) {
        // $constants.* or $variables.* - always refers to scope object on current container.
        targetScope = refContainer.scope;
      } else if (!namespace) {
        // we could be dealing with a builtin var
        // only some builtin vars are backed by actual variables - On $flow/currentPage and $page/currentFlow
        const topLevelScope = tokens[0];
        name = tokens[1] || name;
        if ((topLevelScope === '$page' && name === Constants.CURRENT_FLOW_VARIABLE)
          || (topLevelScope === '$flow' && name === Constants.CURRENT_PAGE_VARIABLE)) {
          targetScope = availableContexts[topLevelScope];
          namespace = Constants.VariableNamespace.BUILTIN;
        }
      } else {
        // To locate the correct scope walk the remainder expression using availableContexts.
        // E.g, $base.application.*, $base.*, $page.* etc will give us a top-level scope that has a getVariable()
        targetScope = tokens.reduce((obj, prop) => obj[prop], availableContexts);
      }

      // return the variable
      if (targetScope && typeof targetScope.getVariable === 'function' && name && namespace) {
        const v = targetScope.getVariable(name, namespace);
        if (v) {
          return {
            variable: targetScope.getVariable(name, namespace),
            name,
            namespace,
          };
        }
      }
      return { name, namespace };
    }
  }

  return RulesUtils;
});

